function waitForRetry(ms, signal) {
  return new Promise(resolve => {
    const finish = () => { clearTimeout(timer); signal?.removeEventListener('abort', finish); resolve(); };
    const timer = setTimeout(finish, ms);
    signal?.addEventListener('abort', finish, { once: true });
    if (signal?.aborted) finish();
  });
}

// Only an explicit successful configuration can disable guest access. Network
// failures must never turn a visitor's selected file into a signup redirect.
export async function fetchGuestAvailability(url, {
  signal, fetchImpl = fetch, wait = waitForRetry, timeoutMs = 6000, onRetry,
} = {}) {
  let errorCode = 'guest_config_unavailable';
  for (let attempt = 1; attempt <= 3; attempt += 1) {
    if (signal?.aborted) return { status: 'cancelled' };
    const abort = new AbortController();
    const cancel = () => abort.abort();
    signal?.addEventListener('abort', cancel, { once: true });
    const timer = setTimeout(cancel, timeoutMs);
    try {
      const response = await fetchImpl(url, { signal: abort.signal, cache: 'no-store' });
      if (response.ok) {
        const config = await response.json();
        if (typeof config?.enabled === 'boolean') {
          if (signal?.aborted) return { status: 'cancelled' };
          return { status: config.enabled ? 'enabled' : 'disabled', attempt_count: attempt };
        }
        errorCode = 'guest_config_invalid';
      } else {
        errorCode = Number.isInteger(response.status) && response.status >= 400 && response.status <= 599
          ? `http_${response.status}` : 'guest_config_unavailable';
      }
    } catch {
      errorCode = abort.signal.aborted ? 'request_timeout' : 'network_failed';
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
    }
    if (signal?.aborted) return { status: 'cancelled' };
    if (attempt < 3) {
      const delay = attempt === 1 ? 1000 : 3000;
      try { onRetry?.({ error_code: errorCode, attempt_count: attempt }); } catch { /* Diagnostics never block access. */ }
      await wait(delay, signal);
    }
  }
  return { status: 'temporarily_unavailable', error_code: errorCode, attempt_count: 3 };
}
